/**
 * ChoiceBoxLayer — a box around each choice point: the situation and the
 * consequences it splits into (§4b).
 *
 * A choice is not a property of one node, it is the fork — the parent and the
 * set of outcomes it opens onto, some wanted, some not. So the box encloses
 * both: the parent card and the column of its direct children. The corner
 * carries the tally, ↑ wanted / ↓ unwanted, and the label says what it is.
 *
 * Derived, never authored. Drawn under the node layer so cards stay on top.
 */
import React from 'react';
import { nodeCardHeight, NODE_CARD_WIDTH } from './nodeCardMetrics';

const PAD = 18;
const STROKE = '#0099ff';

export default function ChoiceBoxLayer({ choicePoints = [], nodes = [], positions = {}, show = false }) {
  if (!show || !choicePoints.length) return null;

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const childrenOf = new Map();
  for (const n of nodes) {
    // Layout nodes carry parentIds (multi-parent); raw pathways carry parentId.
    const pids = Array.isArray(n.parentIds) && n.parentIds.length ? n.parentIds : [n.parentId || n.parent_id].filter(Boolean);
    for (const pid of pids) {
      if (!childrenOf.has(pid)) childrenOf.set(pid, []);
      childrenOf.get(pid).push(n);
    }
  }

  const boxes = [];
  for (const cp of choicePoints) {
    const parent = byId.get(cp.nodeId);
    const kids = childrenOf.get(cp.nodeId) || [];
    const members = [parent, ...kids].filter((n) => n && positions[n.id]);
    if (members.length < 2) continue;
    let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
    for (const n of members) {
      const p = positions[n.id];
      const h = nodeCardHeight(n);
      x0 = Math.min(x0, p.x - NODE_CARD_WIDTH / 2);
      x1 = Math.max(x1, p.x + NODE_CARD_WIDTH / 2);
      y0 = Math.min(y0, p.y - h / 2);
      y1 = Math.max(y1, p.y + h / 2);
    }
    boxes.push({ id: cp.nodeId, x: x0 - PAD, y: y0 - PAD - 10, w: x1 - x0 + PAD * 2, h: y1 - y0 + PAD * 2 + 10, wanted: Array.isArray(cp.wanted) ? cp.wanted.length : (cp.wanted || 0), unwanted: Array.isArray(cp.unwanted) ? cp.unwanted.length : (cp.unwanted || 0) });
  }

  return (
    <g className="choice-box-layer" pointerEvents="none">
      {boxes.map((b) => (
        <g key={b.id}>
          <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={14}
            fill="rgba(0, 153, 255, 0.05)" stroke={STROKE} strokeWidth={1.5} strokeDasharray="8 6" />
          <text x={b.x + 14} y={b.y + 18} fill={STROKE} fontSize={12} fontWeight={700} letterSpacing="0.08em">VALINTA</text>
          <text x={b.x + b.w - 14} y={b.y + b.h - 10} fill={STROKE} fontSize={12} fontWeight={700} textAnchor="end">
            {`↑ ${b.wanted}   ↓ ${b.unwanted}`}
          </text>
        </g>
      ))}
    </g>
  );
}
